import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const sections = [
  { id: 'scope', label: 'Scope' },
  { id: 'information-collected', label: 'Information we collect' },
  { id: 'information-use', label: 'How we use information' },
  { id: 'payments', label: 'Payments' },
  { id: 'information-sharing', label: 'When information is shared' },
  { id: 'browser-storage', label: 'Local storage and similar technology' },
  { id: 'retention', label: 'Retention' },
  { id: 'security', label: 'Security' },
  { id: 'rights', label: 'Your choices and rights' },
  { id: 'international-processing', label: 'International processing' },
  { id: 'children', label: 'Children’s privacy' },
  { id: 'changes', label: 'Changes and contact' },
];

const DataUseNotice = () => (
  <LegalPageLayout
    title="Privacy Policy"
    description="This policy explains what information Clawappz handles, why it is used, and the choices available to platform users."
    sections={sections}
  >
    <section>
      <h2>1. Scope</h2>
      <p>This Privacy Policy applies to Clawappz websites, storefronts, administrative tools, and related services. A business using Clawappz may also have its own privacy notice covering how it uses customer information for its independent operations.</p>
    </section>
    <section>
      <h2>2. Information we collect</h2>
      <ul>
        <li><strong>Account information:</strong> names, email addresses, phone numbers, authentication details, staff roles, and account status.</li>
        <li><strong>Business information:</strong> business identity, contact details, storefront content, catalogues, availability, staff assignments, and payment-setup details.</li>
        <li><strong>Customer and transaction information:</strong> contact details, booking dates and times, requested or purchased items, notes, reference codes, workflow activity, payment status, and transaction history.</li>
        <li><strong>Uploaded content:</strong> logos, product or service images, avatars, and other submitted material.</li>
        <li><strong>Technical information:</strong> device, browser, IP address, diagnostic, security, and service-interaction information.</li>
      </ul>
    </section>
    <section>
      <h2>3. How we use information</h2>
      <ul>
        <li>provide storefronts, bookings, orders, requests, tracking, staff access, and workflows;</li>
        <li>authenticate users and protect accounts and platform security;</li>
        <li>calculate, initiate, verify, and reconcile payments;</li>
        <li>send operational updates and keep records synchronised;</li>
        <li>support users, diagnose faults, prevent misuse, and improve reliability;</li>
        <li>administer subscriptions and enforce platform terms; and</li>
        <li>meet applicable legal, accounting, compliance, and dispute-resolution obligations.</li>
      </ul>
    </section>
    <section>
      <h2>4. Payments</h2>
      <p>Online payment information is entered on the payment provider’s secure service. Clawappz receives information needed to connect a payment to a booking, order, request, or subscription, such as its reference, amount, fees, status, and timestamps. The payment provider processes payment credentials under its own terms and privacy practices.</p>
    </section>
    <section>
      <h2>5. When information is shared</h2>
      <p>Information may be shared with:</p>
      <ul>
        <li>the relevant business and its authorised administrators or staff;</li>
        <li>payment, hosting, storage, communications, security, and technical providers supporting Clawappz;</li>
        <li>advisers, auditors, regulators, courts, or authorities where reasonably necessary or legally required; and</li>
        <li>a successor organisation during a merger, financing, acquisition, restructuring, or platform transfer.</li>
      </ul>
      <p className="mt-3">We do not sell personal information for money.</p>
    </section>
    <section>
      <h2>6. Local storage and similar technology</h2>
      <p>Clawappz uses browser storage to keep users signed in, remember interface preferences, retain cart and booking selections, preserve payment references during redirects, and support installation of the web application. Clearing browser data may sign you out or remove unfinished selections.</p>
    </section>
    <section>
      <h2>7. Retention</h2>
      <p>We retain information for as long as reasonably necessary to provide the platform, maintain transaction and audit records, resolve disputes, protect users, and meet legal or accounting requirements. Retention varies by information type, business relationship, and applicable obligations. Information may be deleted, anonymised, or securely isolated when no longer required.</p>
    </section>
    <section>
      <h2>8. Security</h2>
      <p>We use reasonable administrative, technical, and organisational safeguards designed to protect information. No internet service or storage system can guarantee complete security. Users should use strong credentials, restrict account access, and notify us if they suspect unauthorised activity.</p>
    </section>
    <section>
      <h2>9. Your choices and rights</h2>
      <p>Depending on applicable law, you may be able to request access to, correction of, deletion of, restriction of, or a copy of your personal information, or object to certain processing. Customers may first contact the business with which they placed a booking, order, or request. We may need to verify identity and retain information where law or legitimate record-keeping duties require it.</p>
    </section>
    <section>
      <h2>10. International processing</h2>
      <p>Clawappz and its service providers may process information in countries other than the one where a user is located. Where required, reasonable safeguards are used for such transfers.</p>
    </section>
    <section>
      <h2>11. Children’s privacy</h2>
      <p>Clawappz is not directed to children who cannot lawfully consent to the handling of their information. If you believe a child has provided personal information without appropriate permission, contact us so the matter can be reviewed.</p>
    </section>
    <section>
      <h2>12. Changes and contact</h2>
      <p>We may update this policy as the platform, our practices, or applicable requirements change. The updated date will appear at the top of this page. For privacy questions or requests, email <a href="mailto:support@clawappz.com" className="font-medium text-primary hover:underline">support@clawappz.com</a>.</p>
    </section>
  </LegalPageLayout>
);

export default DataUseNotice;
